<template>
  <sgdf-sidebar
    class="psb"
    :class="variant === 'rail' ? 'psb--rail' : 'psb--full'"
  >
    <template v-for="(section, index) in visibleSections" :key="section.key">
      <!-- Rail : icônes seules, libellé en tooltip, séparées par section -->
      <template v-if="variant === 'rail'">
        <hr v-if="index > 0" class="psb-divider" />
        <sgdf-sidebar-item
          v-for="item in visibleItems(section)"
          :key="item.key"
          :active="item.key === activeKey"
        >
          <sl-tooltip :content="$t(item.labelKey)" placement="right" hoist>
            <NuxtLink class="psb-link psb-link--rail" :to="item.to">
              <sgdf-icon :name="item.icon" />
            </NuxtLink>
          </sl-tooltip>
        </sgdf-sidebar-item>
      </template>

      <!-- Full : section repliable avec titre + libellés -->
      <sl-details
        v-else
        class="psb-details"
        :open="openSections[section.key] ?? true"
        @sl-after-show="setOpen(section.key, true)"
        @sl-after-hide="setOpen(section.key, false)"
      >
        <span slot="summary" class="psb-summary">{{
          $t(section.titleKey)
        }}</span>
        <sgdf-sidebar-item
          v-for="item in visibleItems(section)"
          :key="item.key"
          :active="item.key === activeKey"
        >
          <NuxtLink class="psb-link" :to="item.to">
            <sgdf-icon :name="item.icon" />
            <span class="psb-label">{{ $t(item.labelKey) }}</span>
          </NuxtLink>
        </sgdf-sidebar-item>
      </sl-details>
    </template>
  </sgdf-sidebar>
</template>

<script setup lang="ts">
import "@sgdf/ui-library/sgdf-sidebar.js";
import "@sgdf/ui-library/sgdf-sidebar-item.js";
import "@sgdf/ui-library/sgdf-icon.js";
import type { SidebarSection } from "~/types/sidebar";

const props = defineProps<{
  sections: SidebarSection[];
  /** `full` = sections dépliables avec libellés ; `rail` = icônes seules + tooltip. */
  variant: "rail" | "full";
}>();

const route = useRoute();
const { openSections } = useSidebarStore();

const visibleItems = (section: SidebarSection) =>
  section.items.filter((item) => item.visible !== false);

// N'affiche pas une section (ni son séparateur) si tous ses items sont masqués.
const visibleSections = computed(() =>
  props.sections.filter((section) => visibleItems(section).length > 0),
);

/** Item actif = celui dont le `to` est le plus long préfixe de la route courante. */
const activeKey = computed(() => {
  const path = route.path;
  const match = props.sections
    .flatMap(visibleItems)
    .filter((item) => path === item.to || path.startsWith(item.to + "/"))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return match?.key;
});

const setOpen = (key: string, open: boolean) => {
  openSections.value = { ...openSections.value, [key]: open };
};

// Ouvre automatiquement la section contenant l'item actif.
watch(
  activeKey,
  (key) => {
    if (!key) return;
    const section = props.sections.find((s) =>
      s.items.some((item) => item.key === key),
    );
    if (section && openSections.value[section.key] === false) {
      setOpen(section.key, true);
    }
  },
  { immediate: true },
);
</script>

<style scoped>
/* Le style des items (padding, radius, hover, actif) vient de sgdf-sidebar-item. */
.psb-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: inherit;
  text-decoration: none;
}

.psb-link--rail {
  justify-content: center;
  gap: 0;
}

.psb-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --- Section repliable (mode full) --- */
.psb-summary {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
  color: var(--sgdf-color-gray-500, #6b7280);
}

.psb-details::part(base) {
  border: none;
  background: transparent;
  border-radius: 0;
}

.psb-details::part(header) {
  padding: 0.5rem 0.625rem;
}

.psb-details::part(content) {
  padding: 0;
}

/* --- Rail (icônes seules) --- */
.psb-divider {
  border: none;
  border-top: 1px solid var(--sgdf-color-gray-200, #e5e7eb);
  margin: 0.5rem 0.25rem;
}
</style>
